import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../contexts/LocaleContext';
import { centsToDollarsNumber } from '../../lib/util/money';
import * as types from '../../types';
import styles from './HardwareFirstDisclosure.module.scss';

interface HardwareFirstDisclosureProps {
  discount?: types.HardwareDiscountPreview;
  /** the promotion code the quoted first-tracker price depends on, named under the price */
  promoCode?: string;
}

/**
 * Hardware-first PDP block: server-priced hardware with strikethrough, the required-plan line, and
 * an expandable first-term/renewal price disclosure. Placeholder fidelity — the E6 design pass
 * swaps copy and layout; the i18n keys and structure are the contract.
 */
export default function HardwareFirstDisclosure({ discount, promoCode }: HardwareFirstDisclosureProps) {
  const { t } = useTranslation();
  const { currencyFormatter } = useLocale();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={styles.container}>
      {discount && (
        <div className={styles.priceRow}>
          {discount.listPriceInCents > discount.discountedPriceInCents && (
            <span className={styles.listPrice}>
              {currencyFormatter.format(centsToDollarsNumber(discount.listPriceInCents))}
            </span>
          )}
          <span className={styles.discountedPrice}>
            {currencyFormatter.format(centsToDollarsNumber(discount.discountedPriceInCents))}
          </span>
          {discount.tier === 'first' && discount.listPriceInCents > discount.discountedPriceInCents && promoCode && (
            <span className={styles.withCode}>{t('hardware_first_pdp_with_code', { code: promoCode })}</span>
          )}
        </div>
      )}
      <div className={styles.planLine}>
        {t('hardware_first_plan_required_line')}{' '}
        <button className={styles.learnMore} onClick={() => setExpanded(!expanded)} type="button">
          {t('learn_more')}
        </button>
      </div>
      {expanded && (
        <ul className={styles.termList}>
          <li>{t('hardware_first_membership_after_activation')}</li>
          <li>{t('hardware_first_term_6m')}</li>
          <li>{t('hardware_first_term_1y')}</li>
          <li>{t('hardware_first_term_2y')}</li>
        </ul>
      )}
    </div>
  );
}
